import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { X } from '@phosphor-icons/react';
import { PhoneFrame } from '../PhoneFrame';
import { FormatToggle } from './FormatToggle';
import { PdfDownloadButton } from './PdfDownloadButton';
import type { PdfDownload } from './PdfDownloadButton';
import type { Format } from '../../types/snak';
interface OverlayProps {
  eyebrow: string;
  format: Format;
  onFormatChange: (format: Format) => void;
  onClose: () => void;
  /** In phone format, present the content inside the device. Defaults to true. */
  frame?: boolean;
  /** Attached to the desktop-format scroll container, for scroll tracking. */
  bodyRef?: React.RefObject<HTMLDivElement>;
  /** Adds a "Download PDF" action that captures this print layout. */
  download?: PdfDownload;
  children: React.ReactNode;
}

/** Full-page reading surface for the explanation layers. */
export function Overlay({
  eyebrow,
  format,
  onFormatChange,
  onClose,
  frame = true,
  bodyRef,
  download,
  children
}: OverlayProps) {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = previous;
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);
  const framed = frame && format === 'mobile';
  const content = <motion.div initial={{
    opacity: 0,
    y: 16
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.5,
    delay: 0.08,
    ease: [0.22, 1, 0.36, 1]
  }} data-id="element-618" data-mp="v5e5kmhzkg8g">
      {children}
    </motion.div>;
  return <motion.div role="dialog" aria-modal="true" aria-label={eyebrow} initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.3,
    ease: [0.22, 1, 0.36, 1]
  }} className="fixed inset-0 z-50 flex flex-col bg-[#F7F5F0] font-body" data-id="element-619" data-mp="le9vo1763lkh">
      <div className="flex shrink-0 items-center justify-between gap-3 border-b border-[rgba(1,51,43,0.1)] px-5 py-3 lg:px-10" data-id="element-620" data-mp="1x0sdqitx7fx7">
        <span className="truncate font-body text-[10px] uppercase tracking-[0.24em] text-[rgba(1,51,43,0.45)]" data-id="element-621" data-mp="2216b96xwb7gz">
          {eyebrow}
        </span>
        <div className="flex shrink-0 items-center gap-2" data-id="element-622" data-mp="1s8h3am1k0bqe">
          {download && <PdfDownloadButton {...download} data-id="element-623" data-mp="1ea0w6tzs4j7q" />}
          <FormatToggle value={format} onChange={onFormatChange} data-id="element-624" data-mp="1e86cfj08ux58" />
          <button type="button" onClick={onClose} aria-label="Close" className="inline-flex h-9 items-center gap-2 rounded-full border border-[rgba(1,51,43,0.18)] px-4 font-body text-[10px] uppercase tracking-[0.16em] text-background transition-colors hover:border-[rgba(1,51,43,0.5)] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary" data-id="element-625" data-mp="1j9436jzbkzic">
            <X size={13} weight="bold" aria-hidden="true" data-id="element-626" data-mp="33nnxwrv9ngok" />
            <span className="hidden sm:inline" data-id="element-627" data-mp="s1c2a2ic5bbf">Close</span>
          </button>
        </div>
      </div>

      {framed ? <div className="flex flex-1 items-center justify-center overflow-hidden px-4 py-5" data-id="element-628" data-mp="o0joqx2fkk9c">
          <PhoneFrame label={`${eyebrow} in phone format`} sizeClassName="h-[clamp(500px,calc(100vh-136px),860px)] w-auto aspect-[344/688]" data-id="element-629" data-mp="11roumgwe68s4">
            {content}
          </PhoneFrame>
        </div> : <div ref={bodyRef} className="flex-1 overflow-y-auto" data-id="element-630" data-mp="1bni39p6zpi4m">
          {content}
        </div>}
    </motion.div>;
}